import { createEventHandler } from 'yoshi-flow-editor-runtime/tpa-settings';

import {
  DataHook,
  SettingsEvent,
  ControllerProps,
  IFrameEvent,
  InjectedGlobalSettings,
  Nullable,
  VoidHandler,
  Experiments,
} from '../types';
import {
  CurrentUser,
  RequiredControllerParams,
  FlowAPI,
  WixCodeApi,
  WixExperiments,
} from '../types/controller';
import { RootState } from '../store/root-reducer';
import { bindThunksToStore, Store } from '../store';
import {
  getSetSettingsTabAction,
  getPatchGlobalSettingsAction,
} from '../store/actions';
import { getComputedProps } from '../store/selectors';
import InitialDataFetchService from './initial-data-fetch-service';
import DataSyncService from './data-sync-service';
import { initProps } from './controller-utils';

type PartialGlobalSettings = Partial<InjectedGlobalSettings>;

type MapControllerPropsOptions = RequiredControllerParams & {
  metaSiteId: Nullable<string>;
  state: RootState;
  experiments: WixExperiments;
  dataSyncService: DataSyncService;
  handlers?: ReturnType<typeof bindThunksToStore> & { signUp: VoidHandler };
};

type ControllerPropsWithOptionalHandlers = Omit<ControllerProps, 'handlers'> &
  Partial<Pick<ControllerProps, 'handlers'>>;

type RegisterStoreChangeListenerOptions = RequiredControllerParams & {
  metaSiteId: Nullable<string>;
  store: Store;
  experiments: WixExperiments;
  dataSyncService: DataSyncService;
};

interface RegisterCurrentUserListenerOptions {
  store: Store;
  flowAPI: FlowAPI;
  initialDataFetchService: InitialDataFetchService;
  viewedMemberId: Nullable<string>;
}

const getSignUpHandler = (wixCodeApi: WixCodeApi) => () => {
  wixCodeApi.user.promptLogin({}).catch(() => {});
};

const mapControllerProps = ({
  metaSiteId,
  state,
  experiments,
  handlers,
  controllerConfig,
  flowAPI,
  dataSyncService,
}: MapControllerPropsOptions): ControllerPropsWithOptionalHandlers => {
  const { users, badges, roles, ...controllerProps } = state;
  const { isRTL, isMobile } = flowAPI.environment;
  const { compId } = controllerConfig;

  return {
    ...controllerProps,
    ...(!!handlers && { handlers }),
    compId,
    metaSiteId,
    member: users.viewed.uid ? users.viewed : null,
    rolesMap: roles.map,
    isRTL,
    isMobile,
    isCurrentUserAuthenticated: !!users.current,
    computed: getComputedProps(flowAPI, state, experiments),
    iFrameEvents: dataSyncService.purgeIFrameEvents(),
  };
};

export const registerStoreChangeListener = ({
  metaSiteId,
  store,
  experiments,
  controllerConfig,
  flowAPI,
  dataSyncService,
}: RegisterStoreChangeListenerOptions) => {
  const storeHandlers = bindThunksToStore(store);
  const signUp = getSignUpHandler(controllerConfig.wixCodeApi);
  const initialProps = mapControllerProps({
    metaSiteId,
    state: store.getState(),
    experiments,
    handlers: { ...storeHandlers, signUp },
    controllerConfig,
    flowAPI,
    dataSyncService,
  });
  controllerConfig.setProps(initialProps);

  store.subscribe(() => {
    const updatedProps = mapControllerProps({
      metaSiteId,
      state: store.getState(),
      experiments,
      controllerConfig,
      flowAPI,
      dataSyncService,
    });
    controllerConfig.setProps(updatedProps);
  });

  return storeHandlers;
};

export const registerCurrentUserListener = ({
  store,
  flowAPI,
  initialDataFetchService,
  viewedMemberId,
}: RegisterCurrentUserListenerOptions) => {
  const { wixCodeApi } = flowAPI.controllerConfig;

  wixCodeApi.user.onLogin(async (authenticatedUser: CurrentUser) => {
    const currentMemberId = authenticatedUser.loggedIn
      ? authenticatedUser.id
      : null;

    await initProps({
      store,
      initialDataFetchService,
      currentMemberId,
      viewedMemberId: viewedMemberId ?? currentMemberId,
    });
  });
};

export const registerDataSyncListener = (
  dataSyncService: DataSyncService,
  store: Store,
) => {
  dataSyncService.registerListeners(store);
  store.subscribe(() => dataSyncService.emitEvents(store));
};

export const createSettingsListener = (publicData: Object = {}) => {
  return createEventHandler<SettingsEvent>(publicData);
};

export const registerSettingsListeners = ({
  eventHandler,
  dataSyncService,
  store,
  experiments,
}: {
  eventHandler: ReturnType<typeof createSettingsListener>;
  dataSyncService: DataSyncService;
  store: Store;
  experiments: WixExperiments;
}) => {
  eventHandler.on('tabChange', (settingsTab: DataHook) => {
    store.dispatch(getSetSettingsTabAction(settingsTab));
  });

  eventHandler.on('globalSettings', (settings: PartialGlobalSettings) => {
    const action = getPatchGlobalSettingsAction(settings);

    store.dispatch(action);
    dataSyncService.emitEvent(action);

    if (!experiments.enabled(Experiments.RefreshWidgetsOnSettingsChange)) {
      dataSyncService.addIFrameEvent(IFrameEvent.SetGlobalSettings);
    }
  });

  eventHandler.onReset(() => {
    store.dispatch(getSetSettingsTabAction(null));
  });
};
